import { ProductPackaging } from '../products/product.model';
import { EmployeeBasicInfo } from '../employees/employees.model';
import { Dealer } from '../dealers/dealers.model';
import { Company } from '../production-inventory/production-inventory.model';

export interface OrderDocument {
  id: string;
  file: string;
  filename: string;
  content_type: string;
  size: number;
  description: string;
  document_type: string;
  document_type_display: string;
  uploaded_by_name: string | null;
  content_type_name: string;
  created_at: string;
  updated_at: string;
}

export interface Transporter {
  id: string;
  first_name: string;
  last_name: string;
  vehicle_number: string;
  vehicle_type: string;
  phone_number: string;
  address_line1: string;
  address_line2: string;
  email: string;
  user: string;
}

export enum OrderStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  DISPATCHED = 'dispatched',
}

export enum OrderItemStatus {
  PENDING = 'pending',
  PARTIALLY_DISPATCHED = 'partially_dispatched',
  DISPATCHED = 'dispatched',
  PARTIALLY_DELIVERED = 'partially_delivered',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
}

export interface OrderItem {
  id: string;
  product_packaging: ProductPackaging;
  shipped_quantity: number;
  pending_quantity: number;
  delivered_quantity: number;
  created_at: string;
  updated_at: string;
  box_quantity: number;
  status: OrderItemStatus;
  order: string; // order id
}

export interface Order {
  id: string;
  employee: EmployeeBasicInfo;
  dealer: Dealer;
  company: Company | null;
  items: OrderItem[];
  shipments: Shipment[];
  total_boxes_ordered: number;
  total_boxes_shipped: number;
  total_boxes_delivered: number;
  is_fully_shipped: boolean;
  is_fully_delivered: boolean;
  is_partially_shipped: boolean;
  is_partially_delivered: boolean;
  is_order_cancelled: boolean;
  created_at: string;
  updated_at: string;
  order_number: string;
  order_type: string; // e.g. "general"
  remarks: string;
  preferred_transport: string; // e.g. "Truck"
  dispatch_requested_date: string; // "YYYY-MM-DD"
  delivery_address: string;
  invoice_status: string;
  documents: OrderDocument[];
}

/** One product line of a shipment: which order line, which batch, how many boxes. */
export interface OrderShipmentItemReqBody {
  order_item_id: string;
  warehouse_batch_stock_id: string;
  box_quantity: number;
}

/**
 * Body of `POST orders/items/ship/`. The header travels once and the goods go in `items`,
 * so one request creates one shipment with as many lines as it carries. Two lines may name
 * the same order item with different batches (a split dispatch); the same order item with
 * the same batch twice is rejected.
 */
export interface OrderShipmentReqBody {
  items: OrderShipmentItemReqBody[];
  transporter_id?: string | null;
  dispatch_date?: string | null;
  tracking_number?: string;
  remarks?: string;
}

/** Per-line validation errors, keyed like the request line. Absent keys mean that line is fine. */
export interface OrderShipmentItemErrors {
  order_item_id?: string[];
  warehouse_batch_stock_id?: string[];
  box_quantity?: string[];
}

/**
 * `items` comes back two ways: an array of per-line error objects, positional against the
 * request's `items`, or an array of strings when the whole request is rejected.
 */
export interface OrderShipmentErrorResponse {
  items?: OrderShipmentItemErrors[] | string[];
  detail?: string;
  non_field_errors?: string[];
}

export enum ShipmentStatus {
  PENDING = 'pending',
  PACKED = 'packed',
  DISPATCHED = 'dispatched',
  IN_TRANSIT = 'in_transit',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
}

export interface Shipment {
  id: string;
  shipment_number: string;
  status: ShipmentStatus;
  dispatch_date: string | null;
  delivery_date: string | null;
  tracking_number: string;
  remarks: string;
  transporter: Transporter | null;
  order: Order;
  created_at: string;
  updated_at: string;
  items: ShipmentItem[];
  employee: EmployeeBasicInfo;
}

export interface ShipmentItemBatchInfo {
  id: string;
  production_entry: string;
  production_number: string;
  batch_number: string;
  manufacturing_date: string | null;
  expiry_date: string | null;
}

export interface ShipmentItem {
  id: string;
  box_quantity: number;
  shipment: string;
  created_at: string;
  updated_at: string;
  order_item: OrderItem;
  /** The production entry id. The readable batch fields live on `batch_info`. */
  production_entry: string | null;
  batch_info: ShipmentItemBatchInfo | null;
}

export const OrderTypeTexts: Record<string, string> = {
  general: 'General',
  quantity_scheme: 'Quantity Scheme',
  tour_scheme: 'Tour Scheme',
  net_rate: 'Net Rate',
};

/** Entity filters shared by the pending-orders and dispatched-orders (shipments) sections. */
export interface OrderEntityFilters {
  product: string | null;
  company: string | null;
  dealer: string | null;
  employee: string | null;
  // 'YYYY-MM-DD', the format both <input type="date"> and the API use, so the values pass
  // straight through and sort correctly as strings.
  from_date: string | null;
  to_date: string | null;
}

export function emptyOrderEntityFilters(): OrderEntityFilters {
  return { product: null, company: null, dealer: null, employee: null, from_date: null, to_date: null };
}
